/* app/onboarding/tourSteps.js — какие шаги игрового тура (steps.jsx) видит пространство.

   Шаг может быть привязан к разделу тарифа: `section: 'analytics'`. Если тариф активного
   пространства раздел закрыл (workspace.sections.analytics === false), шаг пропускается —
   иначе обязательный тур просил бы нажать пункт меню, которого нет, и ждал перехода,
   который гард маршрута развернёт на /chat.

   Позиции — КАНОНИЧЕСКИЕ индексы массива STEPS (провайдер хранит именно их и не пересобирает
   при смене пространства), а «доступность» вычисляется на лету. Поэтому переключение
   пространства посреди тура не ломает состояние: закрывшийся шаг переезжает на следующий
   доступный (resolveStepIndex). Чистые функции, без React/DOM — покрыты vitest. */

import { isSectionEnabled } from '../../lib/planSections.js';

export function isStepAvailable(step, workspace) {
  return !!step && (!step.section || isSectionEnabled(workspace, step.section));
}

export function availableSteps(steps, workspace) {
  return (steps || []).filter((s) => isStepAvailable(s, workspace));
}

/** Индекс шага, на котором тур реально стоит: сам index, если доступен, иначе ближайший вперёд, иначе назад. */
export function resolveStepIndex(steps, index, workspace) {
  const list = steps || [];
  if (!list.length) return -1;
  const start = Math.min(Math.max(Number.isFinite(index) ? index : 0, 0), list.length - 1);
  for (let i = start; i < list.length; i += 1) if (isStepAvailable(list[i], workspace)) return i;
  for (let i = start - 1; i >= 0; i -= 1) if (isStepAvailable(list[i], workspace)) return i;
  return -1;
}

/** Следующий (direction>0) или предыдущий доступный индекс от index; на границе остаёмся на месте. */
export function stepIndexAfter(steps, index, direction, workspace) {
  const list = steps || [];
  const cur = resolveStepIndex(list, index, workspace);
  if (cur < 0) return cur;
  const dir = direction < 0 ? -1 : 1;
  for (let i = cur + dir; i >= 0 && i < list.length; i += dir) {
    if (isStepAvailable(list[i], workspace)) return i;
  }
  return cur;
}

/** Прогресс по оставшимся шагам: position (с нуля) и total. */
export function stepPosition(steps, index, workspace) {
  const list = steps || [];
  const cur = resolveStepIndex(list, index, workspace);
  if (cur < 0) return { position: 0, total: 0 };
  let position = 0;
  let total = 0;
  list.forEach((s, i) => {
    if (!isStepAvailable(s, workspace)) return;
    total += 1;
    if (i < cur) position += 1;
  });
  return { position, total };
}

/* Режим «Продукции» (kitchen-ux E1c): gate.kitchenMode — 'pos' | 'classic' (неизвестен — classic).
   В режиме кассы вкладок «Полуфабрикаты» и «Готовые позиции» нет, всё в «Черновиках» — шаг
   «Продукции» несёт подмену step.pos (цель, переход, текст), а число и порядок шагов от режима не
   зависят. classic — тот же объект шага (стабильность useMemo провайдера). */
export function applyKitchenMode(step, workspace) {
  if (!step || !step.pos || !workspace || workspace.kitchenMode !== 'pos') return step;
  const { pos, ...rest } = step;
  return { ...rest, ...pos };
}

/** Канонический индекс шага по id для goTo; закрытый или неизвестный шаг — -1. */
export function indexOfStepId(steps, id, workspace) {
  const i = (steps || []).findIndex((s) => s.id === id);
  return i >= 0 && isStepAvailable(steps[i], workspace) ? i : -1;
}
